import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FiatWalletAccountState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { CreateFiatWalletWithInterstitialModal } from '@sorare/core/src/components/fiatWallet/CreateFiatWalletWithInterstitialModal';
import { InterstitialContextModalMode } from '@sorare/core/src/components/fiatWallet/InterstitialContextModal';
import { useRestrictedAccessContext } from '@sorare/core/src/contexts/restrictedAccess';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const Root = styled(Horizontal).attrs({ center: true, gap: 2 })`
  width: 100%;
  background: var(--c-yellow-400);
  padding: var(--unit) var(--double-unit);

  @media ${tabletAndAbove} {
    padding: var(--double-unit);
  }
`;
const MobileVertical = styled(Vertical).attrs({ gap: 0.5 })`
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;
const UnderlinedBoldLabelM = styled(LabelM).attrs({ bold: true })`
  text-decoration: underline;
`;

type Props = { verificationNeeded?: { kyc: boolean; phoneNumber: boolean } };
export const VerificationBanner = ({ verificationNeeded }: Props) => {
  const { setShowRestrictedAccess } = useRestrictedAccessContext();
  const [showCreateFiatWallet, setShowCreateFiatWallet] = useState(false);

  const { kyc, phoneNumber } = verificationNeeded || {};

  if (!kyc && !phoneNumber) {
    return null;
  }

  return (
    <>
      {!!showCreateFiatWallet && (
        <CreateFiatWalletWithInterstitialModal
          mode={InterstitialContextModalMode.REWARD}
          onDecline={() => setShowCreateFiatWallet(false)}
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          onDismissActivationSuccess={() => setShowCreateFiatWallet(false)}
          onClose={() => setShowCreateFiatWallet(false)}
          canDismissAfterActivation={false}
        />
      )}
      <Root>
        <FontAwesomeIcon
          icon={faExclamationCircle}
          color="var(--c-black)"
          size="lg"
        />
        <MobileVertical>
          <LabelM color="var(--c-black)">
            <FormattedMessage
              id="VerificationBanner.banner.text"
              defaultMessage="Please {kyc, select, true {verify your account} other {}} {both, select, true {and} other {}} {phoneNumber, select, true {validate your phone number} other {}} to claim your reward:"
              values={{ kyc, phoneNumber, both: kyc && phoneNumber }}
            />
          </LabelM>
          <Horizontal gap={0.5}>
            {!!phoneNumber && (
              <ButtonBase onClick={() => setShowRestrictedAccess('phone')}>
                <UnderlinedBoldLabelM color="var(--c-black)">
                  <FormattedMessage
                    id="VerificationBanner.phoneNumber.link"
                    defaultMessage="Verify Phone Number"
                  />
                </UnderlinedBoldLabelM>
              </ButtonBase>
            )}
            {!!kyc && !!phoneNumber && (
              <LabelM color="var(--c-black)">&</LabelM>
            )}
            {!!kyc && (
              <ButtonBase onClick={() => setShowCreateFiatWallet(true)}>
                <UnderlinedBoldLabelM color="var(--c-black)">
                  <FormattedMessage
                    id="VerificationBanner.kyc.link"
                    defaultMessage="Verify Account (KYC)"
                  />
                </UnderlinedBoldLabelM>
              </ButtonBase>
            )}
          </Horizontal>
        </MobileVertical>
      </Root>
    </>
  );
};
